import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Sidebar = ({ currentPage, onNavigate, isMobileOpen, onMobileClose }) => {
    const { user, logout } = useAuth();
    const { isDark } = useTheme();
    const [companyName, setCompanyName] = useState('KEHILLAH PORTAL');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await fetch('/api/v1/settings');
                const data = await response.json();
                if (data.company_name) {
                    setCompanyName(data.company_name);
                }
            } catch (error) {
                console.error('Error fetching company settings:', error);
            }
        };
        fetchSettings();
    }, []);

    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/dashboard/admin' },
        { id: 'owners', label: 'Owners', icon: '👥', path: '/owners' },
        { id: 'cars', label: 'Cars', icon: '🚗', path: '/cars' },
        { id: 'potential', label: 'Potential Drivers', icon: '👤', path: '/potential_drivers' },
        { id: 'drivers', label: 'Drivers', icon: '🧑‍✈️', path: '/drivers' },
        { id: 'sales', label: 'Sales', icon: '💰', path: '/sales' },
        { id: 'settings', label: 'Settings', icon: '⚙️', path: '/settings' },
    ];

    const handleNavigation = (item) => {
        if (item.id === 'dashboard') {
            onNavigate('dashboard');
        } else {
            // For now, use traditional navigation for other pages
            window.location.href = item.path;
        }
        if (onMobileClose) onMobileClose();
    };

    const handleLogout = async () => {
        await logout();
    };

    const styles = {
        sidebar: {
            position: 'fixed',
            top: 0,
            left: isMobileOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRight: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            zIndex: 1000,
            overflowY: 'auto',
        },
        sidebarDesktop: {
            left: 0,
        },
        header: {
            padding: '1.5rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            background: isDark ? '#2d2d2d' : '#f8f9fa',
        },
        logo: {
            fontSize: '1.25rem',
            fontWeight: 'bold',
            background: isDark
                ? 'linear-gradient(135deg, #00d4ff 0%, #7b2ff7 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        nav: {
            flex: 1,
            padding: '1rem 0',
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            padding: '0.75rem 1.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: isDark ? '#d1d5db' : '#374151',
            textDecoration: 'none',
            borderLeft: '3px solid transparent',
        },
        menuItemActive: {
            background: isDark ? '#2d2d2d' : '#f3f4f6',
            borderLeftColor: isDark ? '#00d4ff' : '#8b5cf6',
            color: isDark ? '#00d4ff' : '#8b5cf6',
            fontWeight: 600,
        },
        menuItemHover: {
            background: isDark ? '#2d2d2d' : '#f9fafb',
        },
        icon: {
            fontSize: '1.25rem',
            marginRight: '0.75rem',
            minWidth: '1.5rem',
        },
        footer: {
            padding: '1rem 1.5rem',
            borderTop: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
        },
        userInfo: {
            marginBottom: '1rem',
        },
        userEmail: {
            fontSize: '0.875rem',
            color: isDark ? '#9ca3af' : '#6b7280',
            marginBottom: '0.25rem',
        },
        logoutButton: {
            width: '100%',
            padding: '0.75rem',
            background: isDark ? '#dc2626' : '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 600,
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            display: isMobileOpen ? 'block' : 'none',
        },
    };

    return (
        <>
            {/* Mobile overlay */}
            <div style={styles.overlay} onClick={onMobileClose}></div>

            {/* Sidebar */}
            <div className="sidebar-fixed" style={{ ...styles.sidebar, ...(window.innerWidth >= 1024 ? styles.sidebarDesktop : {}) }}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.logo}>{companyName}</div>
                </div>

                {/* Navigation */}
                <nav style={styles.nav}>
                    {menuItems.map((item) => (
                        <a
                            key={item.id}
                            href={item.path}
                            style={{
                                ...styles.menuItem,
                                ...(currentPage === item.id ? styles.menuItemActive : {}),
                            }}
                            onMouseEnter={(e) => {
                                if (currentPage !== item.id) {
                                    e.currentTarget.style.background = isDark ? '#2d2d2d' : '#f9fafb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (currentPage !== item.id) {
                                    e.currentTarget.style.background = 'transparent';
                                }
                            }}
                        >
                            <span style={styles.icon}>{item.icon}</span>
                            <span>{item.label}</span>
                        </a>
                    ))}
                </nav>

                {/* Footer */}
                <div style={styles.footer}>
                    {user && (
                        <div style={styles.userInfo}>
                            <div style={styles.userEmail}>{user.email}</div>
                        </div>
                    )}
                    <button
                        onClick={handleLogout}
                        style={styles.logoutButton}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDark ? '#b91c1c' : '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = isDark ? '#dc2626' : '#ef4444';
                        }}
                    >
                        <span style={{ marginRight: '0.5rem' }}>🚪</span>
                        Sign Out
                    </button>
                </div>
            </div>

            <style>{`
        @media (min-width: 1024px) {
          .sidebar-fixed {
            left: 0 !important;
          }
        }
      `}</style>
        </>
    );
};

export default Sidebar;
